import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { getFieldName } from 'legacy/lut/components/widgets/public/partner360/planning/helpers/getFieldName'

import { IJBPPlanning, SMBFractionTypes } from 'partner360/entities/planning/model'

import { ISmbFinancialQuery, TGetSmbFinancialValue } from '../model/types'

type TPlanningRowType = ISmbFinancialQuery['type']

const getRowKey = (
  programType: string,
  category: string,
  subCategory: string,
  l3Category: string,
  fYear: number,
  fq: number,
  type: TPlanningRowType,
  smbFraction: ISmbFinancialQuery['smbFraction']
): string =>
  JSON.stringify([programType, category, subCategory, l3Category, fYear, fq, type, smbFraction])

const isPlainFinancialRow = (row: IJBPPlanning): boolean =>
  !row.IsDirect && !row.IsNoSplit && !row.IsRollUp && !row.IsTotal && !row.IsPartnerCount

/**
 * Creates the financial-value reader used by the offering-wide SMB editor directly from the
 * unprepared Planning response. `settingsIndex` always addresses the original, full `formats`
 * array, so opening the editor from one category cannot accidentally resolve another category's
 * row at the same category-local index.
 */
export const createPlanningSmbFinancialResolver = (
  formats: IFormActualsTargetsSettings[],
  planningData: IJBPPlanning[],
  partnerId: string,
  globalCategory?: string | null
): TGetSmbFinancialValue => {
  const rowsByKey = new Map<string, IJBPPlanning>()

  planningData.forEach((row) => {
    if (
      row.PartnerId !== partnerId ||
      !isPlainFinancialRow(row) ||
      (globalCategory != null && row.GlobalCategory !== globalCategory)
    ) {
      return
    }

    const smbFraction = row.SMBFraction ?? SMBFractionTypes.Total
    const addRow = (type: TPlanningRowType) => {
      const key = getRowKey(
        row.ProgramTypes,
        row.Category,
        row.SubCategory,
        row.L3Category,
        row.FYear,
        row.FQuarter,
        type,
        smbFraction
      )

      // Match the legacy reader's sourceData[0] behaviour when malformed duplicate rows arrive.
      if (!rowsByKey.has(key)) {
        rowsByKey.set(key, row)
      }
    }

    if (row.IsTarget) {
      addRow('targets')
    }
    if (row.IsActuals) {
      addRow('actuals')
    }
  })

  return ({ settingsIndex, fq, type, fYear, actualsType, isUSD, smbFraction }) => {
    if (!Number.isInteger(settingsIndex) || settingsIndex < 0) {
      return null
    }

    const setting = formats[settingsIndex]
    if (!setting) {
      return null
    }

    const row = rowsByKey.get(
      getRowKey(
        setting.ProgramType,
        setting.Category,
        setting.SubCategory,
        setting.L3Category,
        fYear,
        fq,
        type,
        smbFraction
      )
    )
    if (!row) {
      return null
    }

    const field = getFieldName({
      actualsType,
      isUnits: false,
      currency: isUSD ? 'USD' : 'LC',
      sType: '',
      isSoB: false,
      isDirect: false,
    })
    const value = (row as unknown as Record<string, number | null | undefined>)[field]

    return value === undefined ? null : value
  }
}
